import { faCreditCard } from '@fortawesome/pro-regular-svg-icons'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import {
  Banner,
  Button,
  FormattedMessage,
  Spinner,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import type { PayMethod } from '@b-stock/payments-methods-api-client'

import PaymentMethodCard from '@/buyer-portal/components/features/order/checkout/PaymentMethods/PaymentMethodCard'
import {
  getDetails,
  getOwner,
  isCreditCardMethod,
} from '@/buyer-portal/components/features/order/checkout/PaymentMethods/utils'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import {
  SELLER_COLLECTS_NS,
  sellerCollectsCopyId,
  type SaleKind,
} from '../sellerCollectsBidCopy'
import type { SellerCollectsFeeAccess } from '../sellerCollectsFeeAccess'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const SavedMethodsTitle = styled.div`
  ${Typography.Subtitle1}
  color: ${Colors.Semantic.Neutral.black};
  margin-bottom: 1rem;
`

const CardsSection = styled.div`
  margin-bottom: 1rem;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem;
`

const NoCardBanner = styled(Banner)`
  margin-bottom: 1rem;
`

const NoCardHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
`

const NoCardTitle = styled.div`
  ${Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.black};
  margin-bottom: 0.25rem;
`

const NoCardBody = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

const AddCardRow = styled.div`
  margin-top: 0.75rem;
`

/**
 * The saved methods Balance can charge the B-Stock fee to: cards, only on the
 * card-only fee picker, and only while the account may use them. The
 * listing's own card setting is not consulted — the fee is invoiced through
 * Balance, not the listing checkout.
 */
export const selectFeeCards = (
  paymentMethods: PayMethod[],
  { isFeeCardOnly, canAddCard }: SellerCollectsFeeAccess
): PayMethod[] =>
  isFeeCardOnly && canAddCard
    ? paymentMethods.filter((method) => isCreditCardMethod(method.type))
    : []

type SellerCollectsFeeCardListProps = {
  /** Already narrowed by {@link selectFeeCards}. */
  feeCards: PayMethod[]
  selectedPaymentMethodId: string | null
  onSelect: (paymentMethodId: string) => void
  /** Opens the credit card form directly; never the Add New drawer. */
  onAddCard: () => void
  isLoading: boolean
  /** The account blocks cards, so there is nothing to add here. */
  isAccountCardBlocked: boolean
  saleKind?: SaleKind
}

const AddCardButton = ({ onAddCard }: { onAddCard: () => void }) => {
  const { trackButtonClicked } = useAnalytics()
  return (
    <Button
      appearance="plainTertiary"
      type="button"
      size="small"
      data-testid="seller-collects-picker-add-card"
      onClick={() => {
        trackButtonClicked({
          buttonName: 'add_new_cc_seller_collects_fee',
          payment_mode: 'CREDIT_CARD',
        })
        onAddCard()
      }}
    >
      <FontAwesomeIcon icon={faPlus} size="sm" />
      <FormattedMessage id={`${SELLER_COLLECTS_NS}.addCard`} />
    </Button>
  )
}

const NoCardState = ({
  isAccountCardBlocked,
  saleKind,
  onAddCard,
}: Pick<
  SellerCollectsFeeCardListProps,
  'isAccountCardBlocked' | 'saleKind' | 'onAddCard'
>) => (
  // The same yellow prompt as the fee section, without its dismiss: here the
  // buyer leaves by Cancel.
  <NoCardBanner type="warning" hideIcon narrow>
    <div data-testid="seller-collects-picker-no-card">
      <NoCardHead>
        <FontAwesomeIcon icon={faCreditCard} aria-hidden="true" />
        <div>
          <NoCardTitle>
            <FormattedMessage id={`${SELLER_COLLECTS_NS}.cardPromptTitle`} />
          </NoCardTitle>
          <NoCardBody>
            <FormattedMessage
              id={
                // A blocked account cannot save a card, so the only thing to say
                // is that Balance emails the invoice.
                isAccountCardBlocked
                  ? `${SELLER_COLLECTS_NS}.cardSkipped`
                  : sellerCollectsCopyId('cardPromptBody', saleKind)
              }
            />
          </NoCardBody>
        </div>
      </NoCardHead>
      {!isAccountCardBlocked && (
        <AddCardRow>
          <AddCardButton onAddCard={onAddCard} />
        </AddCardRow>
      )}
    </div>
  </NoCardBanner>
)

/**
 * The Seller Collects fee picker. Product (2026-10-30): the B-Stock fee is
 * charged by credit card only, so ACH, Net Terms and wire are not rendered
 * here at all — not greyed, not explained. Adding a method goes straight to
 * the card form, so the ACH drawer and its "amount due on your purchases"
 * consent cannot be reached from this flow.
 */
export const SellerCollectsFeeCardList = ({
  feeCards,
  selectedPaymentMethodId,
  onSelect,
  onAddCard,
  isLoading,
  isAccountCardBlocked,
  saleKind = 'bid',
}: SellerCollectsFeeCardListProps) => {
  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    )
  }

  if (feeCards.length === 0) {
    return (
      <NoCardState
        isAccountCardBlocked={isAccountCardBlocked}
        saleKind={saleKind}
        onAddCard={onAddCard}
      />
    )
  }

  return (
    <Container data-testid="seller-collects-fee-card-list">
      <SavedMethodsTitle>
        <FormattedMessage id="BidConfirmationModal.autoPay.modal.savedMethods" />
      </SavedMethodsTitle>
      <CardsSection>
        {feeCards.map((method) => (
          <PaymentMethodCard
            key={method.id}
            name={method.id}
            type={method.type}
            isDefault={!!method.primaryAt}
            isSelected={selectedPaymentMethodId === method.id}
            selected={selectedPaymentMethodId === method.id}
            onClick={onSelect}
            owner={getOwner(method)}
            details={getDetails(method)}
            value={0}
            totalAmountRemaining={0}
            skipAmountValidation
          />
        ))}
      </CardsSection>
      <AddCardButton onAddCard={onAddCard} />
    </Container>
  )
}
